import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
// @mui
import Box from '@mui/material/Box';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import { useSiteSettings, useDocumentMeta } from 'src/hooks/use-site-settings';
import { useChatSocket } from 'src/hooks/useChatSocket';
// redux
import { fetchConversations } from 'src/store/slices/chatSlice';
// components
import { useSettingsContext } from 'src/components/settings';
//
import Main from './main';
import Header from './header';
import NavMini from './nav-mini';
import NavVertical from './nav-vertical';
import NavHorizontal from './nav-horizontal';
import ChatCallGlobal from '../../sections/chat/chat-call-global';

// ----------------------------------------------------------------------

export default function DashboardLayout({ children }) {
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.auth);

  const settings = useSettingsContext();
  const { settings: siteSettings } = useSiteSettings();

  const lgUp = useResponsive('up', 'lg');

  const nav = useBoolean();

  const { pathname } = useLocation();
  const isOnboarding = pathname.includes('/dashboard/onboarding');

  // Initialize global chat socket and fetch initial unread counts
  useChatSocket();

  useEffect(() => {
    if (user?.id) {
      dispatch(fetchConversations({ userId: user.id }));
    }
  }, [dispatch, user?.id]);

  const isHorizontal = settings.themeLayout === 'horizontal';

  const isMini = settings.themeLayout === 'mini';

  // Update document title and favicon based on site settings
  useDocumentMeta(
    siteSettings?.name || 'Field HRM',
    siteSettings?.icon || '/favicon/favicon.ico?v=2'
  );

  const renderNavMini = <NavMini />;

  const renderHorizontal = <NavHorizontal />;

  const renderNavVertical = <NavVertical openNav={nav.value} onCloseNav={nav.onFalse} />;

  // Fullscreen mode for onboarding — hide sidebar, full-width header & main
  if (isOnboarding) {
    return (
      <>
        <Header onOpenNav={nav.onTrue} isOnboarding />

        <Main isOnboarding>{children}</Main>

        <ChatCallGlobal />
      </>
    );
  }

  if (isHorizontal) {
    return (
      <>
        <Header onOpenNav={nav.onTrue} />

        {lgUp ? renderHorizontal : renderNavVertical}

        <Main>{children}</Main>

        <ChatCallGlobal />
      </>
    );
  }

  if (isMini) {
    return (
      <>
        <Header onOpenNav={nav.onTrue} />

        <Box
          sx={{
            minHeight: 1,
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
          }}
        >
          {lgUp ? renderNavMini : renderNavVertical}

          <Main>{children}</Main>
        </Box>

        <ChatCallGlobal />
      </>
    );
  }

  return (
    <>
      <Header onOpenNav={nav.onTrue} />

      <Box
        sx={{
          minHeight: 1,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
        }}
      >
        {renderNavVertical}

        <Main>{children}</Main>
      </Box>

      <ChatCallGlobal />
    </>
  );
}

DashboardLayout.propTypes = {
  children: PropTypes.node,
};
